const { medianDistribution } = require('../../lib/operationsTrends');

export const trendValue = (rows, unit) => {
  if (unit === 'days') {
    const distribution = {};
    rows.forEach(row => Object.entries(row.distribution || {}).forEach(([value, count]) => { distribution[value] = (distribution[value] || 0) + count; }));
    return medianDistribution(distribution);
  }
  return { value: rows.reduce((sum, row) => sum + Math.round(row.value * (unit === 'cad' ? 100 : 1)), 0) / (unit === 'cad' ? 100 : 1), count: null };
};
const CAD = new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', currencyDisplay: 'code' });
export const formatTrendValue = (value, unit) => unit === 'cad' ? CAD.format(value).replace('CAD', 'C$').replace(/\s/g, '') : new Intl.NumberFormat('en-CA', { maximumFractionDigits: unit === 'days' ? 2 : 1 }).format(value);

const COLORS = ['#0273bb', '#1f8104', '#bc4d01', '#8456ce', '#d13313', '#007e80'];
export const buildOperationsChart = ({ trends, measure, fiscalYearStart, monthIndexes, staff = 'all', region = 'all', showTrend = false }) => {
  const months = Array.from({ length: 12 }, (_, index) => {
    const date = new Date(Date.UTC(fiscalYearStart, index + 3, 1));
    return { key: date.toISOString().slice(0, 7), label: date.toLocaleString('en-CA', { month: 'short', timeZone: 'UTC' }) };
  });
  const rows = (trends?.rows || []).filter(row => row.measure === measure.value && (staff === 'all' || row.staffId === staff) && (region === 'all' || row.province === region));
  const asOfMonth = trends?.asOf?.slice(0, 7);
  const coverageStart = trends?.coverage?.[measure.topic]?.slice(0, 7);
  const available = months.map(month => measure.topic === 'funding' || (Boolean(coverageStart) && month.key >= coverageStart && (measure.scheduled || month.key <= asOfMonth)));
  const selectedIndexes = monthIndexes.filter(index => available[index]);
  const category = row => measure.categoryLabel ? row.category : 'Total';
  const categories = [...new Set(rows.filter(row => monthIndexes.some(index => months[index].key === row.month)).map(category))];
  if (!measure.categoryLabel) categories.splice(0, categories.length, 'Total');
  if (measure.value === 'funding') categories.splice(0, categories.length, 'EI', 'CRF');
  if (measure.value === 'applications') categories.splice(0, categories.length, 'Approved', 'Denied', 'Withdrawn');
  // Keep every group individually selectable. Start with the largest five when
  // there are many groups, without replacing the rest with an opaque Other bar.
  const ranked = categories.map(value => ({ value, weight: rows.filter(row => category(row) === value && monthIndexes.some(index => months[index].key === row.month)).reduce((n, row) => n + (measure.unit === 'days' ? Object.values(row.distribution || {}).reduce((a, b) => a + b, 0) : row.value), 0) }))
    .sort((a, b) => b.weight - a.weight || a.value.localeCompare(b.value));
  const selections = categories.map(value => ({ id: JSON.stringify(['category', value]), title: value, matches: row => category(row) === value }));
  const defaultSeriesIds = (ranked.length > 6 ? ranked.slice(0, 5).map(entry => entry.value) : categories)
    .map(value => JSON.stringify(['category', value]));
  if (measure.value === 'applications') {
    // Outcomes are subsets of the receipt-month cohort. Keep every application
    // in Received, including those still waiting, and never sum this derived
    // series back into the underlying monthly totals used by the trend.
    selections.unshift({ id: 'received', title: 'Received', matches: () => true });
    defaultSeriesIds.splice(0, defaultSeriesIds.length, 'received');
  }
  const valuesFor = (index, matches = () => true) => trendValue(rows.filter(row => matches(row) && row.month === months[index].key), measure.unit);
  const series = selections.map((selection, index) => ({
    id: selection.id, title: selection.title, type: 'bar', color: COLORS[index % COLORS.length],
    data: selectedIndexes.map(i => ({ x: months[i].label, y: valuesFor(i, selection.matches).value, samples: valuesFor(i, selection.matches).count })).filter(point => point.y !== null),
  }));
  if (measure.combinedLabel) {
    // Combine the underlying records, never the already-derived series.
    // For duration charts this pools the distributions before taking a median.
    series.unshift({ id: 'combined', title: measure.combinedLabel, type: 'bar', color: '#414d5c',
      data: selectedIndexes.map(i => {
        const { value, count } = valuesFor(i);
        return { x: months[i].label, y: value, samples: count };
      }).filter(point => point.y !== null),
    });
  }
  if (measure.value === 'funding') {
    series.push({ id: 'total-funding', title: 'Total Funding', type: 'bar', color: '#414d5c', data: selectedIndexes.map(i => ({ x: months[i].label, y: valuesFor(i).value })) });
    defaultSeriesIds.push('total-funding');
  }
  if (showTrend && measure.unit !== 'days') {
    const data = selectedIndexes.flatMap(index => {
      const window = [index - 2, index - 1, index];
      // The first recorded calendar month counts regardless of its first activity
      // date. Only the current observed month is partial; schedules cover full months.
      if (window.some(i => i < 0 || !available[i] || (!measure.scheduled && months[i].key >= asOfMonth))) return [];
      return [{ x: months[index].label, y: window.reduce((total, i) => total + valuesFor(i).value, 0) / window.length }];
    });
    if (data.length) series.push({ id: 'trend', isTrend: true, title: 'Trend line', type: 'line', color: '#8456ce', data });
  }
  return { series, defaultSeriesIds, xDomain: selectedIndexes.map(i => months[i].label), months, available,
    partialLabel: !measure.scheduled ? months.find(month => month.key === asOfMonth)?.label : null,
    hasData: rows.some(row => selectedIndexes.some(i => row.month === months[i].key)),
  };
};
